import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, X } from "lucide-react";
import { fmtMDY } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGE = {
  active: "bg-emerald-500/15 text-emerald-300", used: "bg-white/10 text-white/60",
  void: "bg-red-500/15 text-red-300", expired: "bg-amber-500/15 text-amber-300",
};
const inp = "mt-2 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-[#2563EB] [color-scheme:dark]";

function plus12Months() {
  const d = new Date();
  const t = new Date(d.getFullYear() + 1, d.getMonth(), d.getDate());
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
}

export function IssueCreditModal({ enrollment, token, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState(plus12Months());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!enrollment) return null;
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/admin/credits`, { registration_id: enrollment.registration_id, amount: Number(amount), reason, expires_at: expires || null }, { headers: { "X-Admin-Token": token } });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not issue credit.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[85] flex items-start justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="issue-credit-modal" className="relative w-full max-w-md my-16 bg-[#0B0F19] border border-white/10 rounded-3xl p-7">
        <button onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-white"><X size={20} /></button>
        <h2 className="text-xl font-extrabold text-white">Issue Credit</h2>
        <p className="mt-1 text-sm text-white/50">{enrollment.parent_name || enrollment.parent?.name} · {enrollment.athlete_name || enrollment.athlete?.name}</p>
        <label className="mt-5 block text-xs font-bold uppercase tracking-wider text-white/40">Amount (USD)
          <input data-testid="credit-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="150.00" className={inp} />
        </label>
        <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-white/40">Reason
          <input data-testid="credit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Withdrew before first session" className={inp} />
        </label>
        <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-white/40">Expiration date (blank = no expiration)
          <input data-testid="credit-expires" type="date" min={plus12Months()} value={expires} onChange={(e) => setExpires(e.target.value)} className={inp} />
        </label>
        <p className="mt-2 text-xs text-white/40">The Participant Service Agreement sets credits to expire 12 months after issue; a later date or no expiration is allowed.</p>
        {error && <p data-testid="credit-error" className="mt-3 text-sm font-semibold text-red-300">{error}</p>}
        <button data-testid="credit-submit" onClick={submit} disabled={busy || !amount || !reason.trim()} className="mt-6 w-full rounded-full bg-[#2563EB] py-3 font-bold text-white disabled:opacity-50">Issue Credit</button>
      </div>
    </div>
  );
}

export default function CreditsTab({ ctx }) {
  const [data, setData] = useState({ credits: [], balances: [] });
  const [filter, setFilter] = useState("active");
  const headers = { "X-Admin-Token": ctx.token };
  const load = () => axios.get(`${API}/admin/credits`, { headers }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id, status) => {
    const note = window.prompt(status === "used" ? "Applied to which program/registration? (optional)" : "Reason for voiding (optional)") ?? null;
    if (note === null) return;
    try {
      await axios.post(`${API}/admin/credits/${id}/status`, { status, note }, { headers });
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not update credit.");
    }
  };
  const rows = data.credits.filter((c) => filter === "all" || c.status === filter);
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tighter text-white">Credits</h2>
          <p className="mt-2 text-sm font-medium text-white/50">Issue credits from Enrollments → Details. Families redeem by contacting Rep Lab; mark credits used when applied.</p>
        </div>
        <button data-testid="credits-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white"><RefreshCw size={15} /> Refresh</button>
      </div>
      <div data-testid="credit-balances" className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {data.balances.length === 0 && <p className="text-sm text-white/40">No active credit balances.</p>}
        {data.balances.map((b) => (
          <div key={b.guardian_email} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-sm font-bold text-white">{b.guardian_name}</p>
            <p className="text-xs text-white/40">{b.guardian_email}</p>
            <p className="mt-2 text-2xl font-extrabold text-emerald-300">${b.balance.toFixed(2)}</p>
            <p className="text-xs text-white/40">{b.count} active credit{b.count > 1 ? "s" : ""}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-2">
        {["active", "used", "expired", "void", "all"].map((f) => (
          <button key={f} data-testid={`credit-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-xs font-bold capitalize ${filter === f ? "bg-[#2563EB] text-white" : "border border-white/15 text-white/60"}`}>{f}</button>
        ))}
      </div>
      <div className="mt-4 space-y-2">
        {rows.length === 0 && <p className="text-sm text-white/40">No credits.</p>}
        {rows.map((c) => (
          <div key={c.id} data-testid={`credit-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4">
            <div>
              <p className="text-sm font-bold text-white">${Number(c.amount).toFixed(2)} · {c.guardian_name} <span className="text-white/40 font-medium">for {c.athlete_name}</span></p>
              <p className="text-xs text-white/45">{c.reason} · issued {fmtMDY(c.issued_date)} · {c.expires_at ? `expires ${fmtMDY(c.expires_at)}` : "no expiration"}{c.programs?.length ? ` · from ${c.programs.join(", ")}` : ""}</p>
              {(c.used_note || c.void_note) && <p className="text-xs text-white/35">Note: {c.used_note || c.void_note}</p>}
            </div>
            <div className="flex items-center gap-2">
              <span data-testid={`credit-status-${c.id}`} className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${BADGE[c.status]}`}>{c.status}</span>
              {c.status === "active" && <>
                <button data-testid={`credit-use-${c.id}`} onClick={() => act(c.id, "used")} className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-bold text-white/70 hover:text-white">Mark Used</button>
                <button data-testid={`credit-void-${c.id}`} onClick={() => act(c.id, "void")} className="rounded-full border border-red-400/30 px-3 py-1.5 text-xs font-bold text-red-300">Void</button>
              </>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
